import { OnboardingStep1 } from '@/components/onboarding/OnboardingStep1';
import { OnboardingStep2 } from '@/components/onboarding/OnboardingStep2';
import { OnboardingStep3 } from '@/components/onboarding/OnboardingStep3';
import { OnboardingStep4 } from '@/components/onboarding/OnboardingStep4';
import { OnboardingStep5 } from '@/components/onboarding/OnboardingStep5';
import { OnboardingFormData } from '@/components/onboarding/StepContentProps';
import { useAnalytics } from '@/contexts/AnalyticsContext';
import { useSnackbarContext } from '@/contexts/SnackbarContext';
import { UserContext } from '@/contexts/UserContext';
import { Timestamp } from 'firebase/firestore';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { OnboardingCard } from '../components/onboarding/OnboardingCard';
import { OnboardingLayout } from '../components/onboarding/OnboardingLayout';
import { useOnboarding } from '../hooks/useOnboarding';
import { completeOnboarding, createAccount } from '../services/AccountsService';

// All available onboarding steps
const ALL_ONBOARDING_STEPS = [
  'website',
  'scanning',
  'profile',
  'ad-platforms',
  'referral',
] as const;

type OnboardingStep = (typeof ALL_ONBOARDING_STEPS)[number];

// Step order for new accounts (full onboarding flow)
const NEW_ACCOUNT_STEPS: OnboardingStep[] = [
  'website',
  'scanning',
  'profile',
  'ad-platforms',
  'referral',
];

// Step order for existing accounts resuming onboarding (skip account creation)
const RESUME_ONBOARDING_STEPS: OnboardingStep[] = [
  'profile',
  'ad-platforms',
  'referral',
];

interface StepConfig {
  title?: string;
  description?: string;
  noPadding?: boolean;
  hideProgress?: boolean;
}

const STEP_CONFIGS: Record<OnboardingStep, StepConfig> = {
  'website': {
    // eslint-disable-next-line quotes
    title: "Let's start with your digital footprint",
    description:
      'Our AI will analyze your website to extract your brand guidelines and assets automatically.',
  },
  'scanning': {
    noPadding: true,
    hideProgress: true,
  },
  'profile': {
    title: 'Tell us about yourself',
    description: 'Help us personalize your experience.',
  },
  'ad-platforms': {
    title: 'Which ad platforms do you use?',
    description: 'We tailor out agents to your ad channels.',
  },
  'referral': {
    title: 'How did you hear about SuperFunnel?',
    description: '',
  },
};

export default function Onboard() {
  const { show } = useSnackbarContext();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const { accountId: routeAccountId } = useParams<{ accountId?: string }>();
  const { accounts, refetchAccounts } = useContext(UserContext);

  // Check if this is a "continue onboarding" flow (accountId in URL)
  const isResumeOnboarding = !!routeAccountId;

  // Select the appropriate step sequence based on flow type
  const onboardingSteps = isResumeOnboarding
    ? RESUME_ONBOARDING_STEPS
    : NEW_ACCOUNT_STEPS;

  // Get account data if resuming onboarding
  const resumeAccount = isResumeOnboarding
    ? accounts?.find((a) => a.id === routeAccountId)
    : undefined;

  const {
    onboarding,
    setOnboarding,
    loading,
    moveToStep,
    initializeOnboarding,
    accountId,
    accountSlug,
    setAccountId,
    setAccountSlug,
  } = useOnboarding(routeAccountId);

  // Set account slug from account data when resuming
  useEffect(() => {
    if (isResumeOnboarding && resumeAccount && !accountSlug) {
      setAccountSlug(resumeAccount.data().slug);
    }
  }, [isResumeOnboarding, resumeAccount, accountSlug, setAccountSlug]);

  // Initialize onboarding document if it doesn't exist (resume flow)
  useEffect(() => {
    if (
      isResumeOnboarding &&
      resumeAccount &&
      !loading &&
      !onboarding &&
      accountId
    ) {
      const websiteUrl = resumeAccount.data().websiteUrl || '';
      initializeOnboarding(websiteUrl, 'profile');
    }
  }, [
    isResumeOnboarding,
    resumeAccount,
    loading,
    onboarding,
    accountId,
    initializeOnboarding,
  ]);
  const [formData, setFormData] = useState<OnboardingFormData>({
    websiteUrl: '',
    name: '',
    slug: '',
    completedSteps: [],
    currentStep: onboardingSteps[0],
    currentStepIndex: 1,
    industries: [],
    startedAt: Timestamp.now(),
    lastUpdatedAt: Timestamp.now(),
    status: 'in-progress',
    ...onboarding,
  });
  const [isCreating, setIsCreating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);

  // Determine the current step based on stored state or default to first step in the flow
  const getStartingStep = (): OnboardingStep => {
    const storedStep = onboarding?.currentStep as OnboardingStep;
    // If we have a stored step and it's in our current flow, use it
    if (storedStep && onboardingSteps.includes(storedStep)) {
      return storedStep;
    }
    // Otherwise, start from the first step in the current flow
    return onboardingSteps[0];
  };

  const currentStepId: OnboardingStep = getStartingStep();

  // Calculate step index within the current flow (1-based for display)
  const currentStepIndex = onboardingSteps.indexOf(currentStepId) + 1;

  // Track onboarding started (only once on mount)
  useEffect(() => {
    track('onboarding_started', {
      step: currentStepId,
      step_index: currentStepIndex,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync formData with Firestore onboarding data on load/change
  useEffect(() => {
    if (onboarding) {
      setFormData({
        name: '',
        slug: '',
        industries: [],
        ...onboarding,
      });
    }
  }, [onboarding]);

  const transitionToStep = useCallback(
    (nextStep: OnboardingStep, data?: any) => {
      const nextStepIndex = onboardingSteps.indexOf(nextStep) + 1;

      track('onboarding_step_completed', {
        completed_step: currentStepId,
        completed_step_index: currentStepIndex,
        next_step: nextStep,
        next_step_index: nextStepIndex,
        ...data,
      });

      setFadeOut(true);
      setTimeout(async () => {
        await moveToStep(nextStep, nextStepIndex, data);

        track('onboarding_step_viewed', {
          step: nextStep,
          step_index: nextStepIndex,
        });

        setFadeOut(false);
      }, 200);
    },
    [moveToStep, track, currentStepId, currentStepIndex, onboardingSteps],
  );

  const isValidUrl = useCallback((url: string) => {
    if (!url) return false;

    try {
      new URL(url.startsWith('http') ? url : `https://${url}`);
      // add valida url logic using regex. it should be a valid website url
      const urlPattern = new RegExp(
        '^(https?:\\/\\/)?' + // protocol
          '((([a-zA-Z\\d]([a-zA-Z\\d-]*[a-zA-Z\\d])*)\\.)+[a-zA-Z]{2,}|' + // domain name
          '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
          '(\\:\\d+)?(\\/[-a-zA-Z\\d%_.~+]*)*' + // port and path
          '(\\?[;&a-zA-Z\\d%_.~+=-]*)?' + // query string
          '(\\#[-a-zA-Z\\d_]*)?$',
      );

      if (!urlPattern.test(url.startsWith('http') ? url : `https://${url}`)) {
        return false;
      }

      return true;
    } catch {
      return false;
    }
  }, []);

  const handleStep1Submit = async () => {
    track('onboarding_step1_submit_clicked', {
      website_url: formData.websiteUrl,
      has_name: !!formData.name,
      has_slug: !!formData.slug,
    });

    if (isValidUrl(formData.websiteUrl) === false) {
      show('Please enter a valid website URL.', 'warn');
      return;
    }

    setIsCreating(true);
    try {
      const result = await createAccount({
        websiteUrl: formData.websiteUrl,
        name: formData.name || null,
        slug: formData.slug || null,
      });

      await refetchAccounts().catch((err) => {
        console.error('Failed to refetch accounts after creation:', err);
      });

      track('account_created', {
        account_id: result.accountId,
        website_url: formData.websiteUrl,
        slug: result.slug,
      });

      setAccountId(result.accountId);
      setAccountSlug(result.slug || '');

      setFormData((prev) => ({ ...prev, slug: result.slug || '' }));

      // Preload the screenshot image while waiting before transition
      if (result.screenshotUrl) {
        try {
          const img = new Image();
          img.src = result.screenshotUrl;
        } catch (error) {
          console.error('Failed to preload screenshot image:', error);
        }
      }

      // Wait 2-3 seconds to allow the screenshot to load before transitioning
      await new Promise((resolve) => setTimeout(resolve, 2500));

      moveToStep('scanning', onboardingSteps.indexOf('scanning') + 1);
      setOnboarding({
        completedSteps: ['website'],
        currentStep: 'scanning',
        currentStepIndex: 0,
        lastUpdatedAt: Timestamp.now(),
        startedAt: Timestamp.now(),
        status: 'in-progress',
        websiteUrl: formData.websiteUrl,
        screenshotUrl: result.screenshotUrl,
      });

      track('onboarding_step_viewed', {
        step: 'scanning',
        step_index: 2,
      });
    } catch (error) {
      track('account_creation_failed', {
        website_url: formData.websiteUrl,
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      show(
        'An error occurred while creating your account. Please try again.',
        'warn',
      );
      console.error('Failed to create account:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleStep2Next = () => {
    track('onboarding_scanning_completed', {
      website_url: formData.websiteUrl,
    });
    // Just animation, no waiting - move directly to step 3
    transitionToStep('profile');
  };

  const handleStep3Submit = () => {
    track('onboarding_profile_submitted', {
      user_role: formData.userRole,
      company_size: formData.companySize,
      industries: formData.industries,
      industries_count: formData.industries?.length || 0,
    });

    transitionToStep('ad-platforms', {
      userRole: formData.userRole,
      companySize: formData.companySize,
      industries: formData.industries,
    });
  };

  const handleStep4Submit = () => {
    track('onboarding_ad_platforms_submitted', {
      ad_platforms: formData.adPlatforms,
      ad_platforms_count: formData.adPlatforms?.length || 0,
      monthly_ad_spend: formData.monthlyAdSpend,
    });

    transitionToStep('referral', {
      adPlatforms: formData.adPlatforms,
      monthlyAdSpend: formData.monthlyAdSpend,
    });
  };

  const handleStep5Submit = async () => {
    if (!accountId) return;

    track('onboarding_referral_submitted', {
      referral_source: formData.referralSource,
      has_referral_other: !!formData.referralSourceOther,
    });

    setIsSubmitting(true);
    try {
      // Save referral data first
      await moveToStep('referral', onboardingSteps.indexOf('referral') + 1, {
        referralSource: formData.referralSource,
        referralSourceOther: (formData.referralSourceOther || null) as any,
      });
      // Then mark onboarding as complete
      await completeOnboarding(accountId);

      track('onboarding_completed', {
        website_url: formData.websiteUrl,
        user_role: formData.userRole,
        company_size: formData.companySize,
        industries: formData.industries,
        ad_platforms: formData.adPlatforms,
        monthly_ad_spend: formData.monthlyAdSpend,
        referral_source: formData.referralSource,
      });

      // Navigate to workspace
      navigate(`/workspace/${accountSlug}/assistant`);
    } catch (error) {
      track('onboarding_completion_failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });

      show(
        'An error occurred while completing onboarding. Please try again.',
        'warn',
      );
      console.error('Failed to complete onboarding:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackStep = () => {
    const currentIndex = onboardingSteps.indexOf(currentStepId);
    if (currentIndex > 0) {
      track('onboarding_back_clicked', {
        from_step: currentStepId,
        from_step_index: currentStepIndex,
        to_step: onboardingSteps[currentIndex - 1],
        to_step_index: currentIndex,
      });

      transitionToStep(onboardingSteps[currentIndex - 1]);
    }
  };

  const stepConfig = STEP_CONFIGS[currentStepId];

  return (
    <OnboardingLayout>
      <OnboardingCard
        currentStep={currentStepIndex}
        totalSteps={onboardingSteps.length}
        title={stepConfig.title}
        description={stepConfig.description}
        noPadding={stepConfig.noPadding}
        hideProgress={stepConfig.hideProgress}
      >
        <div
          className={`w-full transition-opacity duration-200 ${
            fadeOut ? 'opacity-0' : 'opacity-100'
          }`}
        >
          {currentStepId === 'website' && (
            <OnboardingStep1
              data={formData}
              onChange={setFormData}
              onNext={handleStep1Submit}
              isLoading={isCreating}
              disabled={loading}
            />
          )}
          {currentStepId === 'scanning' && (
            <OnboardingStep2
              data={formData}
              onChange={setFormData}
              onNext={handleStep2Next}
              onBack={handleBackStep}
              disabled={loading}
            />
          )}
          {currentStepId === 'profile' && (
            <OnboardingStep3
              data={formData}
              onChange={setFormData}
              onNext={handleStep3Submit}
              disabled={loading}
            />
          )}
          {currentStepId === 'ad-platforms' && (
            <OnboardingStep4
              data={formData}
              onChange={setFormData}
              onNext={handleStep4Submit}
              onBack={handleBackStep}
              disabled={loading}
            />
          )}
          {currentStepId === 'referral' && (
            <OnboardingStep5
              data={formData}
              onChange={setFormData}
              onNext={handleStep5Submit}
              onBack={handleBackStep}
              isLoading={isSubmitting}
              disabled={loading}
            />
          )}
        </div>
      </OnboardingCard>
    </OnboardingLayout>
  );
}
